import {
  Box,
  Drawer,
  LinearProgress,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { type ReactNode, useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { drawerWidth } from "../config";
import { MainMenu } from "./MainMenu";
import Nav from "./Nav";
import SuspenseBoundary from "./SuspenseBoundary";

interface LayoutProps {
  title?: ReactNode;
  showTitleInNav?: boolean;
  path?: Array<string>;
  back?: string;
  children: ReactNode;
  onBackClick?: () => void;
}

export default function Layout({
  title,
  showTitleInNav = true,
  path,
  back,
  children,
  onBackClick,
}: LayoutProps) {
  const theme = useTheme();
  const mobile = !useMediaQuery(
    `(min-width: ${theme.breakpoints.values.md}px)`
  );
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const pageTitle = (path ?? (typeof title === "string" ? [title] : []))
    .toReversed()
    .join(" · ");

  return (
    <>
      <Helmet
        title={pageTitle}
        titleTemplate="%s · Zeller Baustellen-Informationsprogramm"
      >
        <meta name="theme-color" content={theme.palette.primary.dark} />
      </Helmet>
      <Box sx={{ display: "flex" }}>
        <Drawer
          variant={mobile ? "temporary" : "permanent"}
          open={mobile && open}
          onClose={close}
          PaperProps={{ sx: { width: drawerWidth, borderRight: "none" } }}
          sx={{ width: drawerWidth, zIndex: 1203 }}
        >
          <MainMenu close={close} />
        </Drawer>
        <Box sx={{ flexGrow: 1, position: "relative" }}>
          {showTitleInNav ? (
            <Nav
              title={title}
              back={back}
              mobile={mobile}
              open={open}
              setOpen={setOpen}
              onBackClick={onBackClick}
            />
          ) : (
            <Nav
              back={back}
              mobile={mobile}
              open={open}
              setOpen={setOpen}
              onBackClick={onBackClick}
            />
          )}
          <Toolbar />
          <SuspenseBoundary fallback={<LinearProgress />}>
            <Box component="main" sx={{ my: 2 }}>
              {children}
            </Box>
          </SuspenseBoundary>
        </Box>
      </Box>
    </>
  );
}
